/**
 * La bulle d'un point de carte — le gabarit, partagé par les deux cartes qui en
 * ouvrent une.
 *
 * POURQUOI CE FICHIER N'EST PAS UN COMPOSANT. L'infobulle de Google Maps prend
 * du HTML en chaîne (`infoWindow.setContent`) : elle vit hors de Vue, hors du
 * routeur, hors des styles scopés. D'où les styles en ligne — c'est la seule
 * chose que ce contexte accepte.
 *
 * POURQUOI IL EXISTE. La carte de diffusion d'une mission et celle de la liste
 * des formateurs écrivaient la même bulle deux fois : même police, même en-tête,
 * même badge de type, même filet, même boucle d'étoiles. Elles avaient déjà
 * commencé à diverger — l'une échappait ses valeurs, l'autre non.
 *
 * ÉCHAPPER N'EST PAS FACULTATIF. Le nom, la ville et le département sont saisis
 * par le formateur lui-même depuis son portail ; interpolés bruts dans une
 * chaîne HTML, ils y exécutent ce qu'ils veulent. Chaque valeur qui entre dans
 * ce gabarit passe donc par `echapper`, et les seules exceptions sont les
 * fragments que le gabarit fabrique lui-même (les étoiles, les pastilles).
 */

import { formateurDisplayName as nomAffiche } from '../../utils/formateurDisplay.js'

const POLICE = "'Lexend Deca',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"

const ENTITES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

/** Rend une valeur inoffensive dans du HTML. À appeler sur TOUT ce qui vient de la base. */
export function echapper(valeur) {
  return String(valeur ?? '').replace(/[&<>"']/g, (c) => ENTITES[c])
}

/**
 * Une ligne « libellé / valeur » de la bulle. `valeur` est du HTML déjà échappé.
 *
 * LA VALEUR PLIE, ELLE NE DÉBORDE PAS. Sans `min-width:0`, un élément de flex
 * refuse de descendre sous la largeur de son contenu : une ville au nom long ou
 * un trajet en deux mesures poussait la ligne au-delà de la bulle, et la fin se
 * coupait à droite (retour de relecture du 23/09/2026).
 */
export function ligneBulle(libelle, valeur) {
  return `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#94a3b8;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;white-space:nowrap;flex-shrink:0">${echapper(libelle)}</span><span style="text-align:right;min-width:0;overflow-wrap:anywhere">${valeur}</span></div>`
}

/**
 * Une précision en retrait, en gris clair — « (69) », « à vol d'oiseau »,
 * « (approché) ». `html` est du HTML déjà échappé, ou fabriqué par le gabarit :
 * comme `ligneBulle`, cette aide n'échappe rien, elle habille.
 */
export function mentionDiscrete(html) {
  return `<span style="color:#94a3b8">${html}</span>`
}

/** Les cinq étoiles d'une note sur cinq, ou null quand elle n'est pas connue. */
export function etoilesBulle(note) {
  if (note == null) return null
  return Array.from({ length: 5 }, (_, i) =>
    `<span style="color:${i < Math.round(note) ? '#f59e0b' : '#d1d5db'};font-size:13px">&#9733;</span>`
  ).join('')
}

/** Une pastille colorée : un état qui se lit d'un coup d'œil. */
export function pastilleBulle({ label, fond, couleur, point = null }) {
  const pastille = point
    ? `<span style="width:6px;height:6px;border-radius:50%;background:${point};display:inline-block;flex-shrink:0"></span>`
    : ''
  return `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:500;background:${fond};color:${couleur}">${pastille}${echapper(label)}</span>`
}

/**
 * Le gabarit complet.
 *
 * @param {string}  nom      Le nom affiché (échappé ici).
 * @param {object}  badge    `{ label, fond, couleur }` — le type de fiche, en haut à droite.
 * @param {string}  lignes   Les lignes déjà composées (`ligneBulle`).
 * @param {string}  pied     Ce qui ferme la bulle : une pastille, un lien, une réserve.
 */
export function bulleFormateur({ nom, badge = null, lignes = '', pied = '' }) {
  const titre = echapper(nom) || 'Sans nom'
  const etiquette = badge
    ? `<span style="flex-shrink:0;font-size:11px;font-weight:500;color:${badge.couleur};background:${badge.fond};padding:2px 7px;border-radius:9999px;white-space:nowrap">${echapper(badge.label)}</span>`
    : ''

  // LA MARGE DROITE EST À NOUS. Google ne rembourre la bulle qu'à gauche
  // (12 px) : sans ces 12 px, le contenu touchait le bord droit et l'arrondi
  // en rognait la fin — « Lyon (69 », « / jour » (retour de relecture du
  // 23/09/2026).
  return `
    <div style="font-family:${POLICE};min-width:222px;max-width:292px;padding:2px 12px 4px 0;box-sizing:border-box">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:6px">
        <div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.3;min-width:0;overflow-wrap:anywhere">${titre}</div>
        ${etiquette}
      </div>
      <div style="height:1px;background:#e5e5e5;margin-bottom:8px"></div>
      <div style="font-size:12px;color:#334155;display:flex;flex-direction:column;gap:5px">${lignes}</div>
      ${pied}
    </div>
  `
}

/** Le badge du type de fiche — indépendant ou organisme —, identique sur les deux cartes. */
export function badgeType(type, label) {
  return {
    label,
    fond: type === 'independant' ? '#dbeafe' : '#fef9e7',
    couleur: type === 'independant' ? '#2563eb' : '#d68910',
  }
}

/**
 * La fiche s'ouvre dans un NOUVEL ONGLET (`target="_blank"`) : on garde la
 * carte, son cadrage et la bulle qu'on regardait, pour en comparer plusieurs.
 * C'est le navigateur qui l'ouvre, et le socle le laisse faire (cf.
 * MapFormateursCanvas).
 */
export function lienFiche(f) {
  return `
      <div style="margin-top:10px">
        <a href="/formateurs/${encodeURIComponent(f.id)}" target="_blank" rel="noopener" data-fiche="${echapper(f.id)}" aria-label="Ouvrir la fiche de ${echapper(nomAffiche(f))} dans un nouvel onglet" style="display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:500;color:#2563eb;text-decoration:none;cursor:pointer">
          Ouvrir la fiche <span aria-hidden="true">&#8599;</span>
        </a>
      </div>`
}
